import { GlobalPageLoadExperience } from '@atlassian/ufo';
import { aiFirstCreationStreamingState } from '@databases/database-ai-first-creation-state';
import { useDatabaseStreamState } from '@databases/database-ai-first-creation-ui';
import { getVersionId, useDatabaseContent } from '@databases/database-state';
import { isAIFCStagingAreaView } from '@databases/database-state';
import { setDatabaseContentReady } from '@databases/database-state/state/initial-database-loading-state';
import { isStandalone as isStandaloneCheck } from '@databases/modes';
import { appStoreUtils } from '@databases/redux/store';
import { AsyncOperationStatus } from '@databases/redux-types';
import { useApplySystemTemplate } from '@databases/templates/useApplySystemTemplate';
import { getBridgeAPI } from '@databases/utils/database/bridge';
import { useIsFeatureEnabled } from '@databases/utils/hooks/useIsFeatureEnabled';
import { selectDatabaseByContentIdStatus } from '@databases/utils/redux/databases/selectors';
import useView from '@databases/views/useView';
import React, { type FunctionComponent, useEffect } from 'react';
import { useSnapshot } from 'valtio';

import { DatabaseError } from './DatabaseError';
import { type DatabaseActionKey } from './common/components/database/DatabaseActions';
import DatabaseContent from './common/components/database-content/DatabaseContent';
import DatabaseContentSkeleton from './common/components/database-content/DatabaseContentSkeleton';
import { DatabaseInitialLoadingState } from './common/components/database-skeletons/DatabaseInitialLoadingState';

const isStandalone = isStandaloneCheck();

const actions: DatabaseActionKey[] = ['import', 'print', 'help', 'copy', 'feedback'];
if (isStandalone) {
  actions.push('lock');
}

interface DatabaseContentLoaderProps {
  onDataLoaded?: () => void;
}

const DatabaseContentLoader: FunctionComponent<DatabaseContentLoaderProps> = ({
  onDataLoaded,
}) => {
  const { contentId } = useDatabaseContent();
  const versionId = getVersionId();
  const isDbAiFirstCreationEnabled = useIsFeatureEnabled('dbAiFirstCreation');
  const { firstLayoutProcessed } = useSnapshot(aiFirstCreationStreamingState);
  const { isStreaming } = useDatabaseStreamState();
  const isInAIFCStagingAreaView = isAIFCStagingAreaView();
  const { view } = useView();

  useApplySystemTemplate();

  const databaseStatus = appStoreUtils.useEqualSelector((state) =>
    selectDatabaseByContentIdStatus(state, { contentId })
  );

  useEffect(() => {
    const logMetrics = async () => {
      GlobalPageLoadExperience.mark('database-load-end');
      GlobalPageLoadExperience.success({ force: true });
      (await getBridgeAPI()).onContentLoadSucceeded?.({});
    };

    if (databaseStatus?.status === AsyncOperationStatus.SUCCEEDED) {
      logMetrics();
      onDataLoaded?.();
    }
  }, [databaseStatus?.status, contentId, onDataLoaded]);

  const isDatabaseLoaded = databaseStatus?.status === AsyncOperationStatus.SUCCEEDED;

  const isAIStreamingWithoutLayout =
    isDbAiFirstCreationEnabled &&
    isStreaming &&
    isInAIFCStagingAreaView &&
    // firstLayoutProcessed is always false in standalone, so we should only
    // check for databases in AIFC staging area
    !firstLayoutProcessed;
  const shouldShowSkeleton = !isDatabaseLoaded || isAIStreamingWithoutLayout;
  useEffect(() => {
    if (!shouldShowSkeleton) {
      setDatabaseContentReady();
    }
  }, [shouldShowSkeleton]);

  if (databaseStatus?.status === AsyncOperationStatus.ERROR) {
    return <DatabaseError serializedError={databaseStatus.error} />;
  }

  if (shouldShowSkeleton) {
    return view.id && !isAIStreamingWithoutLayout ? (
      <DatabaseContentSkeleton layoutType={view.settings.layout.type} />
    ) : (
      <DatabaseInitialLoadingState />
    );
  }

  return (
    <DatabaseContent
      contentId={contentId}
      isContentEditable={!versionId}
      actions={actions}
    />
  );
};

export default DatabaseContentLoader;
